import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { NewNGState, NewNGAction } from '../types';

// Simplified initial state
const initialState: NewNGState = {
  // Content
  jobs: [],
  currentJob: null,
  headlines: [],
  programs: [],
  currentProgram: null,

  // Video State
  video: {
    isPlaying: false,
    isMuted: true,
    volume: 0.7,
    currentTime: 0,
    duration: 0,
    isFullscreen: false,
    quality: 'auto'
  },

  // Layout - REMOVED to prevent orientation change remounts
  // layout: {
  //   mode: 'broadcast',
  //   sidebar: true,
  //   ticker: true,
  //   lowerThird: true,
  //   controls: 'overlay'
  // },

  // System
  isLoading: false,
  error: null,
  isLive: false,

  // Preferences
  theme: 'dark',
  language: 'en',
  accessibility: {
    highContrast: false,
    largeText: false,
    reducedMotion: false
  }
};

// Simplified reducer
function newNGReducer(state: NewNGState, action: NewNGAction): NewNGState {
  switch (action.type) {
    case 'SET_JOBS':
      return { ...state, jobs: action.payload };

    case 'SET_CURRENT_JOB':
      return { ...state, currentJob: action.payload };

    case 'SET_HEADLINES':
      return { ...state, headlines: action.payload };

    case 'SET_PROGRAMS':
      return { ...state, programs: action.payload };

    case 'SET_CURRENT_PROGRAM':
      return { ...state, currentProgram: action.payload };

    case 'SET_VIDEO_STATE':
      return { ...state, video: { ...state.video, ...action.payload } };

    // REMOVED SET_LAYOUT to prevent orientation change remounts
    // case 'SET_LAYOUT':
    //   return { ...state, layout: { ...state.layout, ...action.payload } };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_LIVE':
      return { ...state, isLive: action.payload };

    case 'SET_THEME':
      return { ...state, theme: action.payload };

    case 'SET_LANGUAGE':
      return { ...state, language: action.payload };

    case 'SET_ACCESSIBILITY':
      return { ...state, accessibility: { ...state.accessibility, ...action.payload } };

    default:
      return state;
  }
}

// Context
interface NewNGContextType {
  state: NewNGState;
  dispatch: React.Dispatch<NewNGAction>;
}

const NewNGContext = createContext<NewNGContextType | undefined>(undefined);

// Provider component
interface NewNGProviderProps {
  children: ReactNode;
  initialJobId?: string;
}

export const NewNGProvider: React.FC<NewNGProviderProps> = ({ children, initialJobId }) => {
  const [state, dispatch] = useReducer(newNGReducer, initialState);

  // REMOVED: Auto-detect layout mode - this was causing remounts during orientation changes
  // useEffect(() => {
  //   const handleResize = () => {
  //     const isMobile = window.innerWidth < 768;
  //     const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
  //     const mode = isMobile ? 'mobile' : isTablet ? 'web' : 'broadcast';
  //     dispatch({ type: 'SET_LAYOUT', payload: { mode } });
  //   };

  //   handleResize();
  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  // Auto-detect theme
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = (e: MediaQueryListEvent) => {
      dispatch({ type: 'SET_THEME', payload: e.matches ? 'dark' : 'light' });
    };

    if (state.theme === 'auto') {
      dispatch({ type: 'SET_THEME', payload: mediaQuery.matches ? 'dark' : 'light' });
    }

    mediaQuery.addEventListener('change', handleThemeChange);
    return () => mediaQuery.removeEventListener('change', handleThemeChange);
  }, [state.theme]);

  // Set initial job
  useEffect(() => {
    if (initialJobId && state.jobs.length > 0) {
      const job = state.jobs.find(j => j.job_id === initialJobId);
      if (job) {
        dispatch({ type: 'SET_CURRENT_JOB', payload: job });
      }
    }
  }, [initialJobId, state.jobs]);

  return (
    <NewNGContext.Provider value={{ state, dispatch }}>
      {children}
    </NewNGContext.Provider>
  );
};

// Hook
export const useNewNG = () => {
  const context = useContext(NewNGContext);
  if (context === undefined) {
    throw new Error('useNewNG must be used within a NewNGProvider');
  }
  return context;
};